import React, { use, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { useApolloClient, useLazyQuery, useMutation, useQuery } from '@apollo/client/react';
import { orderBy } from '@progress/kendo-data-query';
import cn from 'clsx';
import styles from './style.module.scss';
import { IsPopOut } from '@components-clean/core/page-toolbar/page-toolbar';
import DatasetSelector from '@components-clean/navbar/dataset-selector';
import { DatasetUnion } from '@components-clean/navbar/dataset-selector.utils';
import EnvIndicator from '@components-clean/navbar/env-indicator';
import OrganisationSelector from '@components-clean/navbar/organisation-selector';
import StudySelector from '@components-clean/navbar/study-selector';
import { StudySelectorItem } from '@components-clean/navbar/study-selector-item';
import NavigationService from '@components-clean/page-nav/navigation.service';
import { useLoading } from '@contexts-clean/loader-context';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import {
    getSessionStorageDatasetId,
    getSessionStorageStudyId,
    LocalStateContext,
    NullableOrganisation,
    setSessionStorageDatasetId,
    setSessionStorageStudyId,
    setSessionStorageSubjectCode,
} from '@contexts/local-state-context';
import { UserContext } from '@contexts/user-context';
import { useTranslation } from '@hooks/index';
import Constants from '@utils/constants';
import { Logger } from '@utils/logger';
import NotificationService from '@utils/notification';
import { loadDataSources } from '@utils/study-data.service';
import { notEmpty } from '@utils/utility-helpers';
import { OrganisationMemberFields, NavbarStudyFields } from '@queries/generated/fragments.generated';
import { systemOrganisationsKeyName } from '@queries/generated/org-admin.generated';
import {
    getStudy,
    getStudyVariables,
    userStudiesNavbar,
    userStudiesNavbarVariables,
} from '@queries/generated/studies.generated';
import { selectTRIAdminOrganisation, selectTRIAdminOrganisationVariables } from '@queries/generated/uam.generated';
import { GET_SYSTEM_ORGANISATIONS_KEY_NAME } from '@queries/org-admin';
import { GET_STUDY, GET_USER_STUDIES_NAVBAR } from '@queries/studies';
import '@styles/kendo-theme-opra-legacy/legacy-navbar-overwrite.scss';
import { SET_ADMIN_ORGANISATION } from '@queries/uam';
import { requireDataset, requireOrganisation, requireStudy } from '@config/routes/page-data-helpers';

interface NavbarProps {
    isLegacy?: boolean; // Delete when the new default theme is on all pages
    tabletMode: boolean;
}

const Navbar = ({ isLegacy, tabletMode }: NavbarProps) => {
    const { t } = useTranslation();
    const user = use(UserContext);
    const { dispatch } = use(LocalStateContext);
    const client = useApolloClient();
    const { showLoading, hideLoading } = useLoading();
    const params = new URLSearchParams(window.location.search);
    const datasetCutDate: string | undefined = params.get('datasetcutdate') ?? undefined;

    const hasLegacyUnsavedChangesRef = useRef<boolean>(false);
    const hasUnsavedChangesRef = useRef<boolean>(false);

    // Tracks the study id we last loaded data sources for, so a re-broadcast of the GET_STUDY
    // watchQuery for the same study doesn't trigger a redundant (network-only) data-source reload.
    const lastLoadedStudyIdRef = useRef<string | null>(null);

    const {
        confirmDiscardChanges,
        confirmDiscardChangesLegacy,
        anyComponentHasChanges,
        anyCentralMonitoringToolkitHasChanges,
        anySubjectMonitoringToolkitHasChanges,
        discardChanges,
    } = useUnsavedChangesContext();

    const anyUnsavedChanges = () =>
        anyComponentHasChanges() || anyCentralMonitoringToolkitHasChanges() || anySubjectMonitoringToolkitHasChanges();

    const getDatasetFromQueryString = () => {
        if (IsPopOut()) {
            return datasetCutDate;
        }
        return undefined;
    };

    const [sortedStudies, setSortedStudies] = useState<StudySelectorItem[]>([]);
    const [defaultStudyId, setDefaultStudyId] = useState<string | null>(null);
    const [organisation, setOrganisation] = useState<NullableOrganisation>(null);
    const [currentStudyId, setCurrentStudyId] = useState<string | null>(() => getSessionStorageStudyId());
    const [datasetId, setDatasetId] = useState<string | null | undefined>(() => getSessionStorageDatasetId());
    const [currentDataset, setCurrentDataset] = useState<DatasetUnion | null>(null);
    const [datasets, setDatasets] = useState<DatasetUnion[]>([]);

    const isOrgUser = user?.user?.systemAdminLevel !== undefined ? user?.user?.systemAdminLevel <= 0 : false;
    const currentOrgKey = (organisation?.organisationKey as string) ?? user?.user?.organisationKey;

    const { data: systemOrgsData, loading: systemOrgsLoading } = useQuery<systemOrganisationsKeyName>(
        GET_SYSTEM_ORGANISATIONS_KEY_NAME,
        {
            skip: isOrgUser,
        },
    );
    const systemOrgs =
        systemOrgsData?.systemOrganisations
            ?.filter(notEmpty)
            .toSorted((a, b) => (a.name ?? '').localeCompare(b.name ?? '')) ?? [];

    const [setAdminOrganisation] = useMutation<selectTRIAdminOrganisation, selectTRIAdminOrganisationVariables>(
        SET_ADMIN_ORGANISATION,
    );

    // NAVBAR STATE BASED ON ENVIRONMENT AND LOCATION
    const location = useLocation();

    const showOrganisationSelector = requireOrganisation(location.pathname);
    // Home is a special case - we want the study selector to show,
    // but we don't want it to show "no study configured" message if there are no studies: we still want standard home screen
    const showStudySelector = requireStudy(location.pathname) || location.pathname.includes('/home');
    const showDatasetSelector = requireDataset(location.pathname);

    const { environment, lockedEnvironment } = window;
    const getNavbarEnvClass = (isPopout?: boolean) => {
        if (isPopout) {
            return styles.popoutNavbar;
        }
        if (environment === Constants.environment.PROD) {
            return styles.prodNavbar;
        }
        if (environment !== Constants.environment.PROD && lockedEnvironment) {
            return styles.lockedNavbar;
        }
        if (!lockedEnvironment) {
            return styles.unlockedNavbar;
        }
    };

    // DATASET SELECTION
    const selectDatasetId = (newId: string | null | undefined) => {
        setDatasetId(newId);
        setSessionStorageDatasetId(newId);
    };

    const handleSelectDataset = () => {
        // We want to clear the session storage for the subject code when we change the dataset
        setSessionStorageSubjectCode(null);
    };

    const clearDataset = () => {
        dispatch({
            type: 'SET_SELECTED_DATE',
            datasetDate: null,
        });
        dispatch({
            type: 'SET_SELECTED_CM2_DATASET',
            selectedCM2Dataset: null,
        });
        dispatch({
            type: 'SET_SELECTED_COMPARISON_CM2_DATASET',
            selectedComparisonCM2Dataset: null,
        });
        dispatch({
            type: 'SET_PROCESSED_DATA',
            data: null,
        });
        dispatch({
            type: 'SET_SCORING_DATA',
            data: null,
        });
        selectDatasetId(null);
        setCurrentDataset(null);
        setDatasets([]);
    };

    // ORG SELECTION
    const selectOrganisation = (organisationKey: string, removeDataset?: boolean) => {
        setAdminOrganisation({
            variables: {
                organisationKey,
            },
        })
            .then((result) => {
                if (result?.data?.selectTRIAdminOrganisation) {
                    const newOrg = result.data.selectTRIAdminOrganisation;
                    user.setToken(newOrg.code);
                    user.setUser({
                        ...user.user,
                        organisationId: newOrg.id as string,
                        organisationKey: newOrg.organisationKey as string,
                    });

                    setOrganisation(newOrg);
                    dispatch({ type: 'SELECT_ORGANISATION', organisation: newOrg });
                }
            })
            .catch((err) => {
                Logger.error(err);
                NotificationService.showNotification(t('errorMessage'));
            });

        if (removeDataset) clearDataset();
    };

    useEffect(() => {
        if (systemOrgs?.length && user?.user && !isOrgUser) {
            const key =
                user.user.organisationKey === Constants.ObjectId.Empty ||
                systemOrgs.findIndex((org) => org.key === user.user!.organisationKey) === -1
                    ? (systemOrgs.at(0)?.key as string)
                    : user.user.organisationKey;

            selectOrganisation(key);
        }
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [systemOrgsData]);

    // STUDY SELECTION
    const loadStudiesVariables = useMemo(
        () => ({
            variables: {
                organisationKey: currentOrgKey ?? user.user?.organisationKey,
                userId: user ? user.user?.id : Constants.ObjectId.Empty,
            },
        }),
        [currentOrgKey, user],
    );

    const [loadStudies, { data: studiesData, called: studiesCalled }] = useLazyQuery<
        userStudiesNavbar,
        userStudiesNavbarVariables
    >(GET_USER_STUDIES_NAVBAR);

    const [loadFullStudy, { data: fullStudyData, called: fullStudyCalled, loading: fullStudyLoading }] = useLazyQuery<
        getStudy,
        getStudyVariables
    >(GET_STUDY);

    const selectStudy = (newStudy: StudySelectorItem | undefined | null, removeDataset?: boolean) => {
        const newStudyId = newStudy?.id as string;

        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setCurrentStudyId(newStudyId);
        setSessionStorageStudyId(newStudyId ?? '');

        loadFullStudy({
            variables: {
                id: newStudyId,
            },
        }).catch((err) => window.console.error(err));

        if (removeDataset) clearDataset();
    };

    useEffect(() => {
        const study = fullStudyData?.study;
        dispatch({ type: 'SELECT_STUDY', study });

        // Only (re)load data sources when the selected study actually changes. The GET_STUDY
        // watchQuery can re-broadcast the same result (same study, new object identity) when
        // another query rewrites an overlapping cache entity; without this guard that fires the
        // (network-only) data-source loads twice per study change.
        const studyId = study?.id ? String(study.id) : null;
        if (study && studyId !== lastLoadedStudyIdRef.current) {
            lastLoadedStudyIdRef.current = studyId;
            loadDataSources(client, dispatch, studyId as string)
                .then(() => Logger.log(`Data sources loaded for study ${study.id}`))
                .catch(() => Logger.error(`Data sources load failed for study ${study.id}`));
        }
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [fullStudyData]);

    useEffect(() => {
        const updateStudiesSubscription = NavigationService.onUpdateStudies().subscribe((updateStudiesSubject) => {
            const { message, studyId } = updateStudiesSubject;
            if (message) {
                loadStudies(loadStudiesVariables).catch((err) => window.console.error(err));
                selectOrganisation(message);
            }
            if (studyId) {
                setDefaultStudyId(studyId);
            }
        });

        const unsavedChangesSubscription = NavigationService.onShowWarningLostDataModal().subscribe(
            (unsavedChanges) => {
                hasUnsavedChangesRef.current = unsavedChanges;
            },
        );
        const subscribeUpdateWarningModal = NavigationService.legacyOnShowWarningLostDataModal().subscribe(
            (unsavedChanges: boolean) => {
                hasLegacyUnsavedChangesRef.current = unsavedChanges;
            },
        );

        return () => {
            updateStudiesSubscription.unsubscribe();
            unsavedChangesSubscription.unsubscribe();
            subscribeUpdateWarningModal.unsubscribe();
        };
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, []);

    // MEMBER / DEFAULT STUDY ID
    const members = [...(user.user?.organisation?.members ?? []), ...(user.user?.organisation?.smMembers ?? [])].filter(
        notEmpty,
    ) as OrganisationMemberFields[];

    const member = members.find((item) => item.userId === user.user?.id);

    useEffect(() => {
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        if (member) setDefaultStudyId(member.defaultStudyId as string);
    }, [member]);

    // STUDIES LOADING, SORTING, FILTERING
    useEffect(() => {
        loadStudies(loadStudiesVariables).catch((err) => window.console.error(err));
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [currentOrgKey, loadStudiesVariables]);

    useEffect(() => {
        if (!studiesData) return;

        if (!studiesData.userStudies?.length) {
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/set-state-in-effect
            setSortedStudies([]);
            selectStudy(null);
            return;
        }

        const studiesWithFavourites: StudySelectorItem[] = studiesData.userStudies
            .filter(notEmpty)
            .map((userStudy: NavbarStudyFields) => {
                const studyMember = userStudy.members?.find((m) => m?.userId === user.user?.id) ?? {
                    favourite: false,
                };

                const smStudyMember = userStudy.smMembers?.find((m) => m?.userId === user.user?.id) ?? {
                    favourite: false,
                };

                return {
                    __typename: userStudy.__typename,
                    id: userStudy.id as string,
                    studyNumber: userStudy.studyNumber,
                    favourite: studyMember.favourite ?? smStudyMember.favourite ?? false,
                    _isDefault: defaultStudyId === userStudy.id,
                };
            });

        const allSortedStudies = orderBy(studiesWithFavourites, [
            { field: '_isDefault', dir: 'desc' },
            { field: 'favourite', dir: 'desc' },
            { field: 'studyNumber', dir: 'asc' },
        ]);

        const newStudy = allSortedStudies.find((s) => s.id === currentStudyId) ?? allSortedStudies.at(0);

        selectStudy(newStudy);
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setSortedStudies(allSortedStudies);
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [studiesData, defaultStudyId]);

    useEffect(() => {
        dispatch({
            type: 'SET_USER_STUDIES',
            studies: studiesData?.userStudies?.filter(notEmpty),
        });
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [studiesData]);

    const study = sortedStudies.find((s) => s.id === currentStudyId) ?? null;

    const loading = systemOrgsLoading || !studiesCalled || !fullStudyCalled || fullStudyLoading;

    useEffect(() => {
        if (loading) showLoading();
        else hideLoading();
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [loading]);

    const root = document.documentElement;
    const getLeftPadding = () => {
        let leftPadding: string | undefined;
        let menuWidth = 0;

        if (IsPopOut()) {
            leftPadding = undefined;
        } else if (tabletMode) {
            menuWidth = 59;
        } else {
            menuWidth = 260;
        }

        root.style.setProperty('--left-pagenav-menu-width', `${menuWidth}px`);
        return leftPadding;
    };

    // const getHelpPortalLink = () => {
    //     const isSso = organisation?.security?.authenticationMethod === Authentication.SSO;
    //     return !isOrgUser
    //         ? 'https://internal-support.tritrials.com/support/home'
    //         : isSso
    //         ? 'https://support-sso.tritrials.com/support/home'
    //         : 'https://support.tritrials.com/support/home';
    // };

    const handleSelectOrganisation = async (organisationKey: string) => {
        const anyChanges = anyUnsavedChanges();

        if (anyChanges && !hasLegacyUnsavedChangesRef.current) {
            const confirmed = await confirmDiscardChanges('nav-unsaved-changes');
            if (!confirmed) {
                return false;
            }
            discardChanges();
        } else if (!anyChanges && hasLegacyUnsavedChangesRef.current) {
            const confirmed = await confirmDiscardChangesLegacy();
            if (!confirmed) {
                return false;
            }
        }

        selectOrganisation(organisationKey, true);
        hasLegacyUnsavedChangesRef.current = false;
        setSessionStorageSubjectCode(null);
    };

    const handleSelectStudy = async (newStudy: StudySelectorItem) => {
        const anyChanges = anyUnsavedChanges();

        if (anyChanges && !hasLegacyUnsavedChangesRef.current) {
            const confirmed = await confirmDiscardChanges();
            if (!confirmed) {
                return false;
            }
        } else if (!anyChanges && hasLegacyUnsavedChangesRef.current) {
            const confirmed = await confirmDiscardChangesLegacy();
            if (!confirmed) {
                return false;
            }
        }
        discardChanges();
        selectStudy(newStudy, true);
        setSessionStorageSubjectCode(null);
    };

    return (
        <header
            id="main-header"
            className={cn(styles.navbar, getNavbarEnvClass(IsPopOut()), 'k-w-full', {
                'legacy-navbar': isLegacy, // Delete when the new default theme is on all pages
                'input-borders': !lockedEnvironment,
            })}
            style={{ paddingLeft: getLeftPadding() }}
        >
            <div className={cn(styles.container, 'k-d-flex k-align-items-center k-justify-content-between')}>
                <EnvIndicator />
                <div
                    className={cn(
                        styles.innerContainer,
                        'k-w-full k-d-flex k-align-items-center k-justify-content-end',
                    )}
                >
                    {!isOrgUser && (
                        <OrganisationSelector
                            disabled={!showOrganisationSelector}
                            hideBorder={lockedEnvironment}
                            systemOrgs={systemOrgs}
                            systemOrgsLoading={systemOrgsLoading}
                            selectOrganisation={handleSelectOrganisation}
                            organisationKey={currentOrgKey}
                        />
                    )}
                    <StudySelector
                        disabled={!showStudySelector}
                        isOrgUser={isOrgUser}
                        hideBorder={lockedEnvironment}
                        studies={sortedStudies}
                        selectedStudy={study}
                        loadingStudies={loading}
                        selectStudy={handleSelectStudy}
                    />
                    <DatasetSelector
                        disabled={!showDatasetSelector}
                        isOrgUser={isOrgUser}
                        study={fullStudyData?.study}
                        hideBorder={lockedEnvironment}
                        orgHasNoStudies={!!organisation && organisation.studies?.length === 0}
                        datasetId={datasetId}
                        selectDatasetId={selectDatasetId}
                        datasetCutDate={getDatasetFromQueryString()}
                        currentDataset={showDatasetSelector ? currentDataset : null}
                        setCurrentDataset={setCurrentDataset}
                        datasets={datasets}
                        setDatasets={setDatasets}
                        clearDataset={clearDataset}
                        onChangeCallback={handleSelectDataset}
                    />
                </div>
                {/* <div> */}
                {/*    <a href={getHelpPortalLink()} target="_blank" rel="noreferrer"> */}
                {/*        <div className={styles.helpIcon} aria-label="support portal"> */}
                {/*            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"> */}
                {/* eslint-disable-next-line max-len */}
                {/*                <path d="M480 256A224 224 0 1 0 32 256a224 224 0 1 0 448 0zM0 256a256 256 0 1 1 512 0A256 256 0 1 1 0 256zm168.7-86.2c6.5-24.6 28.7-41.8 54.2-41.8l57.1 0c35.5 0 64 29 64 64.3c0 24-13.4 46.2-34.9 57.2L272 268.3l0 19.7c0 8.8-7.2 16-16 16s-16-7.2-16-16l0-29.5c0-6 3.4-11.5 8.7-14.3l45.8-23.4c10.7-5.4 17.5-16.6 17.5-28.7c0-17.8-14.4-32.3-32-32.3l-57.1 0c-10.9 0-20.5 7.4-23.2 17.9l-.2 .7c-2.2 8.5-11 13.7-19.5 11.4s-13.7-11-11.4-19.5l.2-.7zM232 352a24 24 0 1 1 48 0 24 24 0 1 1 -48 0z" /> */}
                {/*            </svg> */}
                {/*        </div> */}
                {/*    </a> */}
                {/* </div> */}
            </div>
        </header>
    );
};

export default Navbar;
